import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { ExternalLinkIcon, BookmarkIcon, TrashIcon, LightBulbIcon } from '@heroicons/react/outline';
import { Link, useLocation } from 'react-router-dom';
import { renderButton } from '../utils/uiUtils';
import StreamingSummary from './StreamingSummary';
import MarkdownRenderer from './MarkdownRenderer';

const PaperCard = ({ paper }) => {
	const [isFavorite, setIsFavorite] = useState(false);
	const [showSummary, setShowSummary] = useState(false);
	const { user, addFavoritePaper, removeFavoritePaper, refreshUser, eligibility, aiSummarizedPaperIds } = useAuth();
	const isLoggedIn = user !== null;
	const alreadySummarized = aiSummarizedPaperIds.includes(paper.id);

	useEffect(() => {
		setIsFavorite(user?.favoritePapers?.includes(paper.id));
	}, [user, paper.id]);

	const handleAdd = async () => {
		if (!isLoggedIn || !eligibility.favoritePaperAllowed) return;
		try {
			await addFavoritePaper(paper.id);
			setIsFavorite(true);
		} catch (error) {
			console.error('Error adding favorite paper:', error);
		}
	};

	const handleRemove = async () => {
		if (!isLoggedIn) return;
		try {
			await removeFavoritePaper(paper.id);
			setIsFavorite(false);
		} catch (error) {
			console.error('Error removing favorite paper:', error);
		}
	};

	// Generation is free on papers the user was already charged for.
	const canGenerate = isLoggedIn && (alreadySummarized || eligibility.aiSummaryAllowed);

	const handleAISummary = () => {
		if (!canGenerate) return;
		setShowSummary(true);
	};

	const handleSummaryDone = () => {
		// Server-side tracking bumped aiSummarizedPapers — resync so the
		// eligibility hook and downstream UI reflect the new count.
		if (!alreadySummarized) refreshUser();
	};

	const location = useLocation();

	return (
		<div className="group relative bg-white rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover border border-gray-100 hover:border-gray-200">
			{/* Top accent bar */}
			<div className="h-0.5 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

			<div className="p-5 flex flex-col h-full">
				{/* Tags */}
				<div className="mb-3 flex flex-wrap gap-1.5">
					<span className="text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
						{paper.topic}
					</span>
					<span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-50 px-2 py-0.5 rounded">
						{paper.subtopic}
					</span>
				</div>

				{/* Title */}
				<h2 className="mb-2 text-[15px] font-bold text-gray-900 line-clamp-2 leading-snug group-hover:text-purple-900 transition-colors">
					{paper.title}
				</h2>

				{/* Authors & date */}
				<div className="mb-3 text-[11px] text-gray-400 font-medium tracking-wide">
					<span>
						{(() => {
							const authors = paper.Authors?.map(a => a.author) || [paper.first_author];
							const shown = authors.slice(0, 3);
							return shown.join(', ') + (authors.length > 3 ? ` +${authors.length - 3} more` : '');
						})()}
					</span>
					{paper.publish_time && (
						<span className="ml-2 text-gray-300">
							· {new Date(paper.publish_time).toLocaleDateString()}
						</span>
					)}
				</div>

				{/* Abstract */}
				<MarkdownRenderer
					content={paper.abstract}
					compact
					className="text-xs text-gray-500 mb-4 line-clamp-3 leading-relaxed"
				/>

				{/* AI Summary */}
				{showSummary && (
					<div className="mb-4 accent-line pl-4 py-2">
						<h3 className="text-[10px] font-bold mb-1 text-yellow-700 uppercase tracking-widest">AI Summary</h3>
						<StreamingSummary
							paperId={paper.id}
							compact
							onDone={handleSummaryDone}
						/>
					</div>
				)}

				{/* Actions */}
				<div className="mt-auto pt-3 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
					<Link
						to={`/paper/${paper.id}`}
						state={{ from: location }}
						className="inline-flex items-center text-xs font-semibold text-purple-600 hover:text-purple-800 transition-colors group/link"
					>
						<span>Read more</span>
						<ExternalLinkIcon className="w-3 h-3 ml-1 group-hover/link:translate-x-0.5 transition-transform" />
					</Link>
					<div className="flex flex-wrap gap-1.5">
						{renderButton(
							handleAISummary,
							<LightBulbIcon className="w-3 h-3" />,
							showSummary
								? 'Opened'
								: (alreadySummarized ? 'View Summary' : 'AI Summary'),
							'bg-purple-600',
							!isLoggedIn
								? 'Login required'
								: (alreadySummarized ? '' : eligibility.aiSummaryNotAllowedReason)
						)}
						{isFavorite
							? renderButton(handleRemove, <TrashIcon className="w-3 h-3" />, 'Remove', 'bg-red-500', !isLoggedIn ? 'Login required' : '')
							: renderButton(handleAdd, <BookmarkIcon className="w-3 h-3" />, 'Save', 'bg-emerald-600', !isLoggedIn ? 'Login required' : eligibility.favoritePaperNotAllowedReason)
						}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaperCard;
